import { useEffect, useMemo, useState } from 'react'
import {
  gstRegister,
  fyLabel,
  fyPeriod,
  customPeriod,
  GSTPeriod,
  GSTRegisterRow,
  GSTSummary,
} from '../../services/xeroService'
import './GSTRegisterView.css'
import { supabase } from '../../lib/supabase'
import { statusLabel } from '../../lib/dealStatusService'

const money = (n: number | null | undefined) =>
  n === null || n === undefined
    ? '—'
    : `$${Number(n).toLocaleString('en-AU', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`

/** Australian FY starts in July. Aug 2026 → current FY starts 2026. */
function currentFiscalYear(): number {
  const now = new Date()
  const y = now.getFullYear()
  return now.getMonth() >= 6 ? y : y - 1 // month 6 = July
}

export default function GSTRegisterView() {
  const cfy = useMemo(currentFiscalYear, [])
  const fyOptions = useMemo(() => [cfy, cfy - 1, cfy - 2], [cfy])

  // Winnie's review: a custom From/To period, with the fiscal years kept as
  // one-click presets so the common case stays a single choice.
  const [mode, setMode] = useState<'fy' | 'custom'>('fy')
  const [fiscalYear, setFiscalYear] = useState<number>(cfy)
  const [from, setFrom] = useState<string>(`${cfy}-07-01`)
  const [to, setTo] = useState<string>(`${cfy + 1}-06-30`)
  const [rangeError, setRangeError] = useState<string | null>(null)

  /*
   * Open on the last fiscal year that actually holds vehicles.
   *
   * The register opened on the current fiscal year whatever was in the book, so
   * after the stock reconciliation was loaded - all of it January to June 2026,
   * which is FY2025/26 - this screen opened empty and read as broken. The current
   * year is still the default the moment it has rows of its own.
   */
  useEffect(() => {
    let cancelled = false
    const seed = async () => {
      const { data } = await supabase
        .from('deals')
        .select('acquired_date, paid_at, created_at')
        .is('deleted_at', null)
        .order('acquired_date', { ascending: false, nullsFirst: false })
        .limit(1)
      const row = data && data[0]
      if (!row || cancelled) return
      const latest = row.acquired_date || row.paid_at || row.created_at
      if (!latest) return
      const when = new Date(latest)
      const fy = when.getMonth() >= 6 ? when.getFullYear() : when.getFullYear() - 1
      if (fy < cfy && fy >= cfy - 2) {
        setFiscalYear(fy)
        setFrom(`${fy}-07-01`)
        setTo(`${fy + 1}-06-30`)
      }
    }
    seed()
    return () => {
      cancelled = true
    }
  }, [cfy])

  const period: GSTPeriod = useMemo(
    () => (mode === 'fy' ? fyPeriod(fiscalYear) : customPeriod(from, to)),
    [mode, fiscalYear, from, to]
  )
  const [rows, setRows] = useState<GSTRegisterRow[]>([])
  const [summary, setSummary] = useState<GSTSummary | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [exporting, setExporting] = useState(false)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    Promise.all([
      gstRegister.generateRegister(period),
      gstRegister.getSummary(period),
    ])
      .then(([r, s]) => {
        if (cancelled) return
        setRows(r)
        setSummary(s)
      })
      .catch((e) => {
        if (cancelled) return
        setError(e instanceof Error ? e.message : 'Failed to load GST register')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [period])

  const handleExport = async () => {
    setExporting(true)
    setError(null)
    try {
      const csv = await gstRegister.exportCSV(period)
      gstRegister.downloadCSV(csv, period)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Export failed')
    } finally {
      setExporting(false)
    }
  }

  return (
    <div className="gst-register">
      <div className="gst-header">
        <div>
                      <h2>Margin Scheme (GST Register)</h2>
          <p className="gst-sub">
            Second-hand goods — notional 1/11th input credit on purchases, GST on resales.
            Export as CSV for the accountant to review before lodging the BAS.
          </p>
        </div>
        <div className="gst-controls">
          <div className="gst-mode">
            <button
              className={`gst-mode-btn${mode === 'fy' ? ' on' : ''}`}
              onClick={() => setMode('fy')}
            >
              Fiscal year
            </button>
            <button
              className={`gst-mode-btn${mode === 'custom' ? ' on' : ''}`}
              onClick={() => {
                // Seed the custom range from whichever FY is showing, so the
                // switch never lands the user on an empty or surprising period.
                setFrom(`${fiscalYear}-07-01`)
                setTo(`${fiscalYear + 1}-06-30`)
                setMode('custom')
              }}
            >
              Custom period
            </button>
          </div>

          {mode === 'fy' ? (
            <select
              id="fy-select"
              value={fiscalYear}
              onChange={(e) => setFiscalYear(parseInt(e.target.value, 10))}
            >
              {fyOptions.map((fy) => (
                <option key={fy} value={fy}>
                  {fyLabel(fy)}
                </option>
              ))}
            </select>
          ) : (
            <div className="gst-range">
              <label>
                <span>From</span>
                <input
                  type="date"
                  value={from}
                  max={to}
                  onChange={(e) => {
                    const v = e.target.value
                    setRangeError(v > to ? 'The start date is after the end date.' : null)
                    setFrom(v)
                  }}
                />
              </label>
              <label>
                <span>To</span>
                <input
                  type="date"
                  value={to}
                  min={from}
                  onChange={(e) => {
                    const v = e.target.value
                    setRangeError(v < from ? 'The end date is before the start date.' : null)
                    setTo(v)
                  }}
                />
              </label>
            </div>
          )}

          <button
            className="btn-export"
            onClick={handleExport}
            disabled={exporting || loading || rows.length === 0}
          >
            {exporting ? 'Exporting…' : 'Export CSV'}
          </button>
        </div>
      </div>

      {rangeError && <div className="gst-range-error">{rangeError}</div>}

      <p className="gst-period-line">
        Showing <strong>{period.label}</strong>. A tax-invoice purchase is claimed in the period it
        was bought; a margin-scheme credit is claimed in the period the vehicle sold.
      </p>

      {summary && summary.unconfirmed_treatments > 0 && (
        <div className="gst-alert">
          <strong>{summary.unconfirmed_treatments}</strong> of these vehicles have a GST treatment
          that was inferred from the seller type and never confirmed by anyone. They are marked
          <em> inferred</em> in the table — worth checking before the BAS is lodged.
        </div>
      )}

      {summary && (
        <div className="gst-tiles">
          <div className="tile">
            <span className="tile-label">Vehicles</span>
            <span className="tile-value">{summary.total_vehicles}</span>
            <span className="tile-note">
              {summary.vehicles_sold} sold · {summary.vehicles_in_stock} in stock
            </span>
          </div>
          <div className="tile">
            <span className="tile-label">Total Purchases</span>
            <span className="tile-value">{money(summary.total_purchases)}</span>
          </div>
          <div className="tile">
            <span className="tile-label">GST on Purchases (1B)</span>
            <span className="tile-value accent">{money(summary.total_credits_claimed)}</span>
            <span className="tile-note">capped at GST on sale</span>
          </div>
          <div className="tile">
            <span className="tile-label">Credits Deferred</span>
            <span className="tile-value">{money(summary.total_credits_deferred)}</span>
            <span className="tile-note">on stock not yet sold</span>
          </div>
          <div className="tile">
            <span className="tile-label">Total Sales (G1)</span>
            <span className="tile-value">{money(summary.total_sales_g1)}</span>
            <span className="tile-note">full sale prices, realised sales only</span>
          </div>
          <div className="tile">
            <span className="tile-label">GST on Sales (1A)</span>
            <span className="tile-value">{money(summary.total_gst_on_sales)}</span>
            <span className="tile-note">realised sales only</span>
          </div>
          <div className="tile">
            <span className="tile-label">Net GST Payable</span>
            <span className="tile-value">{money(summary.total_net_gst_payable)}</span>
            <span className="tile-note">GST on sales − credits</span>
          </div>
        </div>
      )}

      {error && <div className="gst-alert error">{error}</div>}

      {loading ? (
        <div className="gst-loading">Loading register…</div>
      ) : rows.length === 0 ? (
        <div className="gst-alert">
          Nothing falls in {period.label}. A vehicle appears here in the period its credit is
          attributable to — the period of purchase for a tax invoice, the period of sale for a
          margin-scheme acquisition.
        </div>
      ) : (
        <div className="gst-table-wrap">
          <table className="gst-table">
            <thead>
              <tr>
                  {/* Winnie's 10 Sep order: the purchase side reads left to right
                      Purchase -> Purchase Date -> Margin Scheme Credit -> GST on
                      Purchases (1B) -> Claimed In, then the sales side in its own
                      block. The old uncapped Notional column is gone: it is the legacy
                      figure the spec retires, and it invited reading the row as though
                      the credit were one eleventh of the purchase price with no cap. */}
                  <th>Vehicle</th>
                  <th>Stock No</th>
                  <th>VIN</th>
                  <th>Seller</th>
                  <th>GST Treatment</th>
                  <th className="num">Purchase</th>
                  <th>Purchase Date</th>
                  <th className="num" title="The gross the credit is worked from: purchase price on a tax invoice, or the lesser of purchase and sale under the margin scheme">
                    Margin Scheme Credit
                  </th>
                  <th className="num" title="One eleventh of Margin Scheme Credit - what can actually be claimed in this period">
                    GST on Purchases (1B)
                  </th>
                  <th>Claimed In</th>
                  <th className="num">Sale Price</th>
                  <th>Sold Date</th>
                  <th className="num" title="Total Sales: the full, uncapped sale price. Not the same figure as Margin Scheme Credit, and never used to work one out">
                    G1 (BAS)
                  </th>
                  <th className="num">GST on Sales (1A)</th>
                  <th className="num">Net GST Payable</th>
                  <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.deal_id}>
                  <td>{r.vehicle || '—'}</td>
                  <td>{r.stock_number}</td>
                  <td className="mono">{r.vin || '—'}</td>
                  <td>{r.seller_type || '—'}</td>
                  <td>
                    <span className={`gst-treat gst-treat-${r.gst_treatment}`}>
                      {r.gst_treatment_label}
                    </span>
                    {r.treatment_inferred && <em className="gst-inferred" title="Backfilled from the seller type — never confirmed">inferred</em>}
                  </td>
                        <td className="num">{money(r.purchase_price)}</td>
                        <td>{r.purchase_date || '—'}</td>
                        <td className="num">{money(r.margin_scheme_credit)}</td>
                        <td className="num accent" title={r.note}>
                          {money(r.div66_credit)}
                          {r.credit_capped && <em className="gst-capped" title="Limited to the GST payable on the sale">capped</em>}
                        </td>
                        <td>
                          {r.attributes_to === 'sale'
                            ? `Sale · ${r.attribution_date || '—'}`
                            : r.attributes_to === 'purchase'
                              ? `Purchase · ${r.attribution_date ? String(r.attribution_date).slice(0, 10) : r.purchase_date}`
                              : 'Not yet claimable'}
                        </td>
                        <td className="num">
                          {money(r.sale_price)}
                          {r.sale_basis === 'projected' && (
                            <em className="basis basis-projected" title="An estimated sale price, not an invoiced one. It carries no credit and is excluded from the net GST.">
                              projected
                            </em>
                          )}
                        </td>
                        <td>{r.sale_date || '—'}</td>
                        <td className="num">{money(r.g1_bas)}</td>
                        <td className="num">{money(r.gst_on_sale)}</td>
                        <td className="num">{money(r.net_gst_payable)}</td>
                        {/* The vehicle's status comes from the canonical registry in dealStatusService,
                          not from whether this row happens to carry a sale date. A row could read
                          Sold while the deal sat at Paid (Ready for Sale), which is exactly the
                          case the credit-timing fix in xeroService turns on. */}
                        <td>{statusLabel(r.deal_status)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="gst-footnote">
        Projected sale figures use the estimated sale price and are excluded from the net GST
        payable totals. Only realised sales (issued invoices) contribute to GST payable. Final
        figures should be verified by the accountant before the BAS is lodged.
      </p>
    </div>
  )
}
